import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Field';
import { useAvailableBrands } from './queries';
import { useFeatures, useVehicleCategories } from '@/features/platform/queries';
import type { SearchCriteria } from './criteria';
import { FUEL_LABELS, TRANSMISSION_LABELS } from '@/config/platform';

/**
 * Panneau de filtres. Chaque modification réécrit l'URL : l'état de la
 * recherche reste entièrement dans la barre d'adresse.
 */
export function SearchFilters({
  criteria,
  onChange,
  onReset,
}: {
  criteria: SearchCriteria;
  onChange: (patch: Partial<SearchCriteria>) => void;
  onReset: () => void;
}) {
  const categories = useVehicleCategories();
  const features = useFeatures();
  const brands = useAvailableBrands(criteria.lieu);

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink-900">Type de véhicule</h3>
        <div className="flex flex-wrap gap-2">
          {(categories.data ?? []).map((category) => {
            const active = criteria.categories.includes(category.slug);
            return (
              <button
                key={category.slug}
                type="button"
                onClick={() => onChange({ categories: toggle(criteria.categories, category.slug) })}
                className={
                  active
                    ? 'rounded-full border border-brand-600 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-800'
                    : 'rounded-full border border-ink-200 px-3 py-1.5 text-sm text-ink-700 hover:border-ink-300'
                }
              >
                {category.label}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink-900">Boîte de vitesses</h3>
        <div className="flex gap-2">
          {(['manual', 'automatic'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ boite: criteria.boite === value ? undefined : value })}
              className={
                criteria.boite === value
                  ? 'flex-1 rounded-xl border border-brand-600 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-800'
                  : 'flex-1 rounded-xl border border-ink-200 px-3 py-2 text-sm text-ink-700'
              }
            >
              {TRANSMISSION_LABELS[value]}
            </button>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3">
        <Input
          label="Prix min / jour"
          type="number"
          min={0}
          step={5000}
          placeholder="0"
          value={criteria.prixMin ?? ''}
          onChange={(e) => onChange({ prixMin: e.target.value ? Number(e.target.value) : undefined })}
        />
        <Input
          label="Prix max / jour"
          type="number"
          min={0}
          step={5000}
          placeholder="—"
          value={criteria.prixMax ?? ''}
          onChange={(e) => onChange({ prixMax: e.target.value ? Number(e.target.value) : undefined })}
        />
      </section>

      <section className="grid grid-cols-2 gap-3">
        <Select
          label="Carburant"
          value={criteria.carburant ?? ''}
          onChange={(e) =>
            onChange({ carburant: (e.target.value || undefined) as SearchCriteria['carburant'] })
          }
        >
          <option value="">Tous</option>
          {Object.entries(FUEL_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          label="Places minimum"
          value={criteria.places ?? ''}
          onChange={(e) => onChange({ places: e.target.value ? Number(e.target.value) : undefined })}
        >
          <option value="">Peu importe</option>
          {[2, 4, 5, 7, 9, 15].map((n) => (
            <option key={n} value={n}>
              {n} places et plus
            </option>
          ))}
        </Select>
      </section>

      {(brands.data ?? []).length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink-900">Marque</h3>
          <div className="max-h-44 space-y-1.5 overflow-y-auto pr-1">
            {(brands.data ?? []).map((brand) => (
              <label key={brand} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={criteria.marques.includes(brand)}
                  onChange={() => onChange({ marques: toggle(criteria.marques, brand) })}
                />
                {brand}
              </label>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink-900">Équipements</h3>
        <div className="space-y-1.5">
          {(features.data ?? [])
            .filter((feature) => feature.is_filterable)
            .map((feature) => (
              <label key={feature.slug} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={criteria.equipements.includes(feature.slug)}
                  onChange={() => onChange({ equipements: toggle(criteria.equipements, feature.slug) })}
                />
                {feature.label}
              </label>
            ))}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink-900">Formule</h3>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={criteria.chauffeur ?? false}
              onChange={(e) => onChange({ chauffeur: e.target.checked || undefined })}
            />
            Avec chauffeur
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={criteria.livraison ?? false}
              onChange={(e) => onChange({ livraison: e.target.checked || undefined })}
            />
            Livraison possible
          </label>
        </div>
      </section>

      <Button variant="secondary" className="w-full" onClick={onReset}>
        Réinitialiser les filtres
      </Button>
    </div>
  );
}
